// Simple event-based loading state manager
type ListenerFn = (isLoading: boolean) => void;

class LoadingStateManager {
  private isLoading: boolean = false;
  private pendingNavigations: number = 0;
  private listeners: ListenerFn[] = [];
  private debounceTimer: NodeJS.Timeout | null = null;

  // Get current loading state
  getIsLoading(): boolean {
    return this.isLoading;
  }

  // Get pending navigation count
  getPendingNavigations(): number {
    return this.pendingNavigations;
  }

  // Start tracking a new navigation
  startNavigation(): void {
    this.pendingNavigations++;
    // console.log(`LoadingStateManager: Starting navigation, pending count: ${this.pendingNavigations}`);
    this.updateLoadingState();
  }

  // Complete a navigation
  completeNavigation(): void {
    if (this.pendingNavigations > 0) {
      this.pendingNavigations--;
    }
    // console.log(`LoadingStateManager: Completing navigation, pending count: ${this.pendingNavigations}`);
    this.updateLoadingState();
  }

  // Set loading state directly (use with caution)
  setIsLoading(value: boolean): void {
    // console.log(`LoadingStateManager: Setting loading state directly to ${value}`);
    
    // Reset pending navigations when manually setting state
    if (!value) {
      this.pendingNavigations = 0;
    }
    
    this.isLoading = value;
    this.notifyListenersDebounced();
  }

  // Update loading state based on pending navigations
  private updateLoadingState(): void {
    const newLoadingState = this.pendingNavigations > 0;
    
    if (this.isLoading !== newLoadingState) {
      // console.log(`LoadingStateManager: Updating loading state to ${newLoadingState} based on pending navigations`);
      this.isLoading = newLoadingState;
      this.notifyListenersDebounced();
    }
  }

  // Subscribe to loading state changes
  subscribe(listener: ListenerFn): () => void {
    // console.log(`LoadingStateManager: Adding subscriber, total: ${this.listeners.length + 1}`);
    this.listeners.push(listener);
    
    // Immediately notify the new subscriber of the current state
    setTimeout(() => {
      listener(this.isLoading);
    }, 0);
    
    // Return unsubscribe function
    return () => {
      // console.log('LoadingStateManager: Removing subscriber');
      this.listeners = this.listeners.filter(l => l !== listener);
    };
  }
  
  // Debounced notification to prevent rapid updates
  private notifyListenersDebounced(): void {
    // Clear any existing debounce timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    
    // Use a small timeout to prevent rapid changes from causing multiple renders
    this.debounceTimer = setTimeout(() => {
      this.notifyListeners();
      this.debounceTimer = null;
    }, 10);
  }

  // Notify all listeners of state change
  private notifyListeners(): void {
    // console.log(`LoadingStateManager: Notifying ${this.listeners.length} listeners of state: ${this.isLoading} (pending: ${this.pendingNavigations})`);
    this.listeners.forEach(listener => {
      try {
        listener(this.isLoading);
      } catch (error) {
        // console.error('Error in loading state listener:', error);
      }
    });
  }
}

// Create a singleton instance
export const loadingStateManager = new LoadingStateManager();

// Convenience methods
export const getLoadingState = (): boolean => loadingStateManager.getIsLoading();
export const setLoadingState = (isLoading: boolean): void => loadingStateManager.setIsLoading(isLoading);
export const startNavigation = (): void => loadingStateManager.startNavigation();
export const completeNavigation = (): void => loadingStateManager.completeNavigation(); 